import React from "react"
import { Lock } from "lucide-react"
import { useCurrency } from "@/context/currency/currency-context"
import { InfoTip } from "@/components/custom/fire/info-tip"
import type { YearData } from "@/lib/projection-math"

interface MilestoneData {
	pct: number
	year: number
	income: number
	reached: boolean
}

interface FireMilestonesProps {
	data: YearData[]
	annualGoal: number
	currentIncome: number
}

// 5% — Rocket on launch pad with small exhaust flames
function IconIgnition() {
	return (
		<svg
			width="48"
			height="48"
			viewBox="0 0 48 48"
			fill="none"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			<rect x="20" y="18" width="8" height="16" rx="2" fill="#60a5fa" />
			<polygon points="24,8 20,18 28,18" fill="#f87171" />
			<polygon points="20,30 16,36 20,34" fill="#3b82f6" />
			<polygon points="28,30 32,36 28,34" fill="#3b82f6" />
			<circle cx="24" cy="24" r="2.5" fill="#e2e8f0" />
			<ellipse cx="22" cy="36" rx="2" ry="3" fill="#fbbf24" opacity="0.9" />
			<ellipse cx="26" cy="36" rx="2" ry="3" fill="#f97316" opacity="0.9" />
			<ellipse cx="24" cy="38" rx="1.5" ry="2" fill="#fbbf24" opacity="0.7" />
			<rect x="14" y="39" width="20" height="3" rx="1" fill="#64748b" />
		</svg>
	)
}

// 10% — Rocket ascending with curved trajectory trail
function IconLiftOff() {
	return (
		<svg
			width="48"
			height="48"
			viewBox="0 0 48 48"
			fill="none"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M14 42 Q18 32 26 20" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="3 2" fill="none" />
			<g transform="translate(28,17) rotate(30)">
				<rect x="-4" y="-8" width="8" height="14" rx="2" fill="#60a5fa" />
				<polygon points="0,-14 -4,-8 4,-8" fill="#f87171" />
				<polygon points="-4,4 -8,8 -4,6" fill="#3b82f6" />
				<polygon points="4,4 8,8 4,6" fill="#3b82f6" />
				<circle cx="0" cy="-2" r="2" fill="#e2e8f0" />
				<ellipse cx="-1.5" cy="7" rx="1.5" ry="2.5" fill="#fbbf24" opacity="0.9" />
				<ellipse cx="1.5" cy="7" rx="1.5" ry="2.5" fill="#f97316" opacity="0.9" />
			</g>
		</svg>
	)
}

// 25% — Rocket with pressure arc lines (Max Q)
function IconMaxQ() {
	return (
		<svg
			width="48"
			height="48"
			viewBox="0 0 48 48"
			fill="none"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M10 26 Q16 14 24 11" stroke="#94a3b8" strokeWidth="1.5" fill="none" opacity="0.6" />
			<path d="M8 33 Q14 19 24 16" stroke="#64748b" strokeWidth="1.5" fill="none" opacity="0.5" />
			<path d="M12 40 Q17 28 24 23" stroke="#94a3b8" strokeWidth="1" fill="none" opacity="0.35" />
			<rect x="20" y="14" width="8" height="16" rx="2" fill="#60a5fa" />
			<polygon points="24,6 20,14 28,14" fill="#f87171" />
			<polygon points="20,26 15,34 20,31" fill="#3b82f6" />
			<polygon points="28,26 33,34 28,31" fill="#3b82f6" />
			<circle cx="24" cy="21" r="2.5" fill="#e2e8f0" />
			<ellipse cx="22" cy="32" rx="1.5" ry="2.5" fill="#fbbf24" opacity="0.9" />
			<ellipse cx="26" cy="32" rx="1.5" ry="2.5" fill="#f97316" opacity="0.9" />
		</svg>
	)
}

// 50% — Rocket flying diagonally with stars appearing
function IconStratosphere() {
	return (
		<svg
			width="48"
			height="48"
			viewBox="0 0 48 48"
			fill="none"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			<circle cx="10" cy="12" r="1" fill="#e2e8f0" />
			<circle cx="38" cy="8" r="1.5" fill="#e2e8f0" />
			<circle cx="42" cy="22" r="1" fill="#e2e8f0" />
			<circle cx="8" cy="30" r="1" fill="#e2e8f0" />
			<circle cx="40" cy="36" r="1" fill="#e2e8f0" opacity="0.6" />
			<g transform="translate(24,22) rotate(-45)">
				<rect x="-4" y="-8" width="8" height="14" rx="2" fill="#60a5fa" />
				<polygon points="0,-14 -4,-8 4,-8" fill="#f87171" />
				<polygon points="-4,4 -8,8 -4,6" fill="#3b82f6" />
				<polygon points="4,4 8,8 4,6" fill="#3b82f6" />
				<circle cx="0" cy="-2" r="2" fill="#e2e8f0" />
				<ellipse cx="-1.5" cy="7" rx="1.5" ry="2.5" fill="#fbbf24" opacity="0.9" />
				<ellipse cx="1.5" cy="7" rx="1.5" ry="2.5" fill="#f97316" opacity="0.9" />
			</g>
		</svg>
	)
}

// 100% — Astronaut helmet with yellow star
function IconZeroGravity() {
	return (
		<svg
			width="48"
			height="48"
			viewBox="0 0 48 48"
			fill="none"
			aria-hidden="true"
			xmlns="http://www.w3.org/2000/svg"
		>
			<defs>
				<radialGradient id="visor-grad" cx="40%" cy="35%" r="65%">
					<stop offset="0%" stopColor="#1e3a5f" />
					<stop offset="100%" stopColor="#0f172a" />
				</radialGradient>
			</defs>
			<circle cx="24" cy="23" r="16" fill="#94a3b8" />
			<circle cx="24" cy="23" r="13" fill="#64748b" />
			<ellipse cx="24" cy="24" rx="9" ry="8" fill="url(#visor-grad)" />
			<ellipse cx="20" cy="19" rx="3" ry="2" fill="white" opacity="0.12" />
			<rect x="14" y="35" width="20" height="5" rx="3" fill="#64748b" />
			<rect x="18" y="36.5" width="12" height="2" rx="1" fill="#3b82f6" opacity="0.6" />
			<polygon
				points="39,5 40.5,8.5 44,8.5 41.2,10.8 42.2,14.2 39,12 35.8,14.2 36.8,10.8 34,8.5 37.5,8.5"
				fill="#fbbf24"
			/>
		</svg>
	)
}

const MILESTONE_CONFIG: Record<
	number,
	{
		icon: () => React.JSX.Element
		stage: string
		reachedMessage: string
		gradient: string
		reachedBg: string
		reachedBorder: string
		reachedText: string
	}
> = {
	5: {
		icon: IconIgnition,
		stage: "Ignition",
		reachedMessage: "First burn. The countdown is over.",
		gradient: "linear-gradient(135deg, #fef9c3, #fde68a)",
		reachedBg: "bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/10",
		reachedBorder: "border-yellow-300 dark:border-yellow-700",
		reachedText: "text-yellow-700 dark:text-yellow-400",
	},
	10: {
		icon: IconLiftOff,
		stage: "Lift-off",
		reachedMessage: "Off the pad — momentum is yours now.",
		gradient: "linear-gradient(135deg, #fde68a, #fbbf24)",
		reachedBg: "bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-900/20",
		reachedBorder: "border-amber-300 dark:border-amber-700",
		reachedText: "text-amber-700 dark:text-amber-400",
	},
	25: {
		icon: IconMaxQ,
		stage: "Max Q",
		reachedMessage: "Peak pressure, peak progress. Through the hardest part.",
		gradient: "linear-gradient(135deg, #fbbf24, #f59e0b)",
		reachedBg: "bg-gradient-to-br from-amber-100 to-orange-50 dark:from-amber-900/25 dark:to-orange-900/20",
		reachedBorder: "border-amber-400 dark:border-amber-600",
		reachedText: "text-amber-700 dark:text-amber-300",
	},
	50: {
		icon: IconStratosphere,
		stage: "Stratosphere",
		reachedMessage: "Half the sky is yours. The air thins, the view opens.",
		gradient: "linear-gradient(135deg, #f59e0b, #d97706)",
		reachedBg: "bg-gradient-to-br from-orange-50 to-amber-100 dark:from-orange-900/20 dark:to-amber-900/30",
		reachedBorder: "border-orange-400 dark:border-orange-600",
		reachedText: "text-orange-700 dark:text-orange-300",
	},
	100: {
		icon: IconZeroGravity,
		stage: "Zero Gravity",
		reachedMessage: "Weightless. Your dividends orbit on their own.",
		gradient: "linear-gradient(135deg, #d97706, #92400e)",
		reachedBg: "bg-gradient-to-br from-amber-100 to-yellow-200 dark:from-amber-900/30 dark:to-yellow-900/30",
		reachedBorder: "border-amber-500 dark:border-amber-500",
		reachedText: "text-amber-800 dark:text-amber-300",
	},
}

export function FireMilestones({ data, annualGoal, currentIncome }: FireMilestonesProps) {
	const { format } = useCurrency()

	if (annualGoal <= 0) return null

	const convertedGoal = annualGoal // pre-converted to display currency in FirePage
	const convertedCurrent = currentIncome

	const milestones: MilestoneData[] = [5, 10, 25, 50, 100].map((pct) => {
		const target = convertedGoal * (pct / 100)
		const yearData = data.find((d) => d.annualIncome >= target)
		return {
			pct,
			year: yearData?.year ?? 0,
			income: yearData ? yearData.annualIncome : 0,
			reached: convertedCurrent >= target,
		}
	})

	return (
		<div className="space-y-3">
			<p className="text-sm font-medium text-muted-foreground flex items-center gap-1">
				Mission Milestones
				<InfoTip content="Progress checkpoints toward FIRE. Each represents a percentage of your annual expense goal covered by projected dividend income." />
			</p>
			<div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
				{milestones.map((m, idx) => {
					const config = MILESTONE_CONFIG[m.pct]
					const isLast = idx === milestones.length - 1

					return (
						<div
							key={m.pct}
							className={`p-4 rounded-xl border text-center transition-all duration-300
                ${isLast ? "col-span-2 sm:col-span-1" : ""}
                ${
					m.reached ? `${config.reachedBg} ${config.reachedBorder} shadow-sm` : "bg-muted/20 border-border/40"
				}`}
						>
							{m.reached ? (
								<>
									<div className="flex justify-center mb-1">
										<config.icon />
									</div>
									<p
										className="text-xl font-black bg-clip-text text-transparent"
										style={{ backgroundImage: config.gradient }}
									>
										{m.pct}%
									</p>
									<p className={`text-xs font-semibold mt-0.5 ${config.reachedText}`}>
										{config.stage}
									</p>
									<p className="text-xs text-muted-foreground mt-0.5">
										{format((m.pct / 100) * convertedGoal, { skipConversion: true })}/yr
									</p>
									<p className={`text-xs mt-2 leading-tight italic ${config.reachedText}`}>
										"{config.reachedMessage}"
									</p>
								</>
							) : (
								<>
									<div className="flex justify-center mb-1">
										<Lock className="h-5 w-5 text-muted-foreground/30" />
									</div>
									<p className="text-xl font-black text-muted-foreground/40">{m.pct}%</p>
									<p className="text-xs text-muted-foreground/50 font-medium">{config.stage}</p>
									{m.year > 0 && (
										<p className="text-xs text-muted-foreground/60 mt-0.5">Est. {m.year}</p>
									)}
									<p className="text-xs text-muted-foreground/50 mt-0.5">
										{format((m.pct / 100) * convertedGoal, { skipConversion: true })}/yr
									</p>
								</>
							)}
						</div>
					)
				})}
			</div>
		</div>
	)
}
